import React, { useRef, useCallback } from 'react';

/**
 * Hook to manage auto-continue logic after content generation.
 * Handles the timing and state management for automatically advancing
 * to the next step after certain operations (like content generation).
 */
export const useAutoContinue = (): {
  willAutoContinueRef: React.MutableRefObject<boolean>;
  triggerAutoContinue: (
    onNextStep: () => Promise<void>,
    onStateChange?: {
      setProcessing: (value: boolean) => void;
      setLoading: (value: boolean) => void;
    }
  ) => Promise<void>;
} => {
  const willAutoContinueRef = useRef(false);

  const triggerAutoContinue = useCallback(
    async (
      onNextStep: () => Promise<void>,
      onStateChange?: {
        setProcessing: (value: boolean) => void;
        setLoading: (value: boolean) => void;
      }
    ): Promise<void> => {
      console.log('[useAutoContinue] Triggering auto-continue');

      // Mark that we will auto-continue (prevents finally block from setting loading to false)
      willAutoContinueRef.current = true;

      // Update state if callbacks provided
      if (onStateChange) {
        // CRITICAL: Set processing FIRST, then loading to false
        // React 18 batches these automatically, but setting processing first ensures
        // that if there's any timing issue, we show processing state
        console.log('[useAutoContinue] Setting processing=true');
        onStateChange.setProcessing(true);
        console.log('[useAutoContinue] Setting loading=false');
        onStateChange.setLoading(false);
      }

      try {
        await onNextStep();
        console.log('[useAutoContinue] Auto-continue completed');
      } catch (err) {
        console.error('[useAutoContinue] Error in auto-continue:', err);
        throw err;
      } finally {
        if (onStateChange) {
          onStateChange.setProcessing(false);
        }
        willAutoContinueRef.current = false;
      }
    },
    []
  );

  return {
    willAutoContinueRef,
    triggerAutoContinue,
  };
};
